import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Landmark, Users, ArrowRight, Check } from 'lucide-react';
import { services as staticServices, processSteps } from '../data/siteData';
import { Button } from '../components/ui/button';
import { contentApi } from '../services/api';

const iconMap = {
  Building2,
  Landmark,
  Users,
};

export default function ServicesPage() {
  const [servicesData, setServicesData] = useState({
    heading: "Our Services",
    subheading: "From concept to completion, we offer comprehensive real estate services tailored to your needs.",
    services: staticServices
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentApi.getSection('services_page')
      .then(res => {
        if (res && res.content) {
          setServicesData(res.content);
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Header */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
            What We Offer
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 font-outfit mt-4">
            {servicesData.heading}
          </h1>
          <p className="text-lg text-slate-600 font-manrope mt-4 max-w-2xl">
            {servicesData.subheading}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {(servicesData.services || staticServices).map((service, index) => {
              const Icon = iconMap[service.icon] || Building2;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  data-testid={`service-${service.id}`}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="w-16 h-16 bg-[#F0F8FF] rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-[#47ace2]" />
                    </div>
                    <h2 className="text-3xl font-semibold text-slate-900 font-outfit mb-4">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 font-manrope leading-relaxed mb-6">
                      {service.fullDesc}
                    </p>
                    <ul className="space-y-3">
                      {(service.features || []).map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-700 font-manrope">
                          <div className="w-5 h-5 rounded-full bg-[#47ace2]/10 flex items-center justify-center">
                            <Check className="w-3 h-3 text-[#47ace2]" />
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100">
                      <img
                        src={
                          service.image || (
                            index === 0
                              ? 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80'
                              : index === 1
                              ? 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80'
                              : 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80'
                          )
                        }
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
              How We Work
            </span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight font-outfit mt-4">
              Our Process
            </h2>
          </div>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-700 hidden lg:block" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative text-center" data-testid={`process-step-${index}`}>
                  <div className="w-16 h-16 mx-auto bg-[#47ace2] rounded-full flex items-center justify-center text-2xl font-bold font-outfit relative z-10">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold font-outfit mt-6 mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-sm font-manrope">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#F0F8FF]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight text-slate-900 font-outfit mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-slate-600 font-manrope mb-8">
            Whether you're looking to build, invest, or develop, our team is here to help 
            you every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#47ace2] hover:bg-[#1f7db3] rounded-full px-8">
                Get in Touch
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button variant="outline" className="border-[#47ace2] text-[#47ace2] hover:bg-white rounded-full px-8">
                View Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
